import React, { createContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { convertLength, convertWeight, calculateArea, round } from '../utils/calculatorUtils';
import { calculateSectionCompletionStatus } from '../utils/sectionCompletionUtils';
import { v4 as uuidv4 } from 'uuid';

// Helper for basic deep equality check (adjust if complex types like Dates/Functions are needed)
function deepEqual(a, b) {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object' || a === null || b === null) return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!deepEqual(a[key], b[key])) return false;
  }

  return true;
}

// Create the context
const ProCalculatorContext = createContext();

// Define the default first cookie OUTSIDE and BEFORE ProCalculatorProvider to ensure a stable ID for linking
const initialDefaultCookieId = uuidv4();
const initialDefaultCookieRow = {
  id: initialDefaultCookieId,
  cookieName: 'Cookie Name',
  type: 'rectangle',
  width: '',
  length: '',
  diameter: '',
  unit: 'in',
  notes: '',
  isMetric: false
};

// Define the provider function
function ProCalculatorProvider({ 
  children, 
  isDarkMode = false, 
  savedState = {}, 
  onStateChange = null, 
  isSubscribed = false, 
  user = null, 
  subscriptionDetails = null,
  activeSection: externalActiveSection = null,
  setActiveSection: externalSetActiveSection = null 
}) {
  // FIXED UNITS: Length=inches, Weight=grams
  const isMetric = false; // Always inches
  const isMetricWeight = true; // Always grams

  // Calculate if user is within 30-day free trial period
  const isInFreeTrial = user && new Date(user.created_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  
  // Subscription status message
  const subscriptionStatus = user 
    ? subscriptionDetails?.type === 'admin'
      ? '⚡ Admin Access (Full Pro Features)'
      : isInFreeTrial 
        ? '✅ Enjoy your 30-day free trial!'
        : isSubscribed
          ? '✅ Pro subscription active'
          : '🔒 Please subscribe to continue using Pro features'
    : '🔒 Please log in to access all Pro features';

  // Refs for managing state updates
  const didMountRef = useRef(false);
  const isSyncingRef = useRef(false);
  const prevSavedStateRef = useRef(null);
  const referenceIcingPerAreaRef = useRef(0);

  // ==================== STATE DEFINITIONS ====================
  // Section visibility and completion state
  const [internalActiveSection, setInternalActiveSection] = useState('reference');
  const [sectionCompletionStatus, setSectionCompletionStatus] = useState({});

  // Use external state if provided, otherwise use internal state
  const activeSection = externalActiveSection ?? internalActiveSection;
  const setActiveSection = externalSetActiveSection ?? setInternalActiveSection;

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);

  // Available sections - 'all' section removed as requested
  const sections = [
    { id: 'reference', label: 'Reference Cookie' },
    { id: 'cookieNames', label: 'Cookie Names' },
    { id: 'sets', label: 'Sets' },
    { id: 'dimensions', label: 'Dimensions' },
    { id: 'colors', label: 'Color %' },
    { id: 'orders', label: 'Orders' },
    { id: 'results', label: 'Results' }
  ];

  // Default reference cookie values (in inches and grams)
  const defaultReference = {
    width: '2.5',     // Inches
    length: '3.5',    // Inches
    weightNoIcing: '40', // Grams
    weight: '54'      // Grams
  };

  // Reference cookie state - Input expected in inches/grams
  const [useDefaultReference, setUseDefaultReference] = useState(() => savedState?.useDefaultReference ?? false);
  const [reference, setReference] = useState(() => {
    const defaultRef = {
      width: '',
      length: '',
      weightNoIcing: '',
      weight: ''
    };
    // Use saved state if it exists, otherwise use empty defaults
    return savedState?.reference || defaultRef;
  });

  // Cookie related state
  const [cookieRows, setCookieRows] = useState(() => {
    const rows = savedState?.cookieRows;
    const initialValue = Array.isArray(rows) && rows.length > 0 ? rows : [initialDefaultCookieRow];
    return initialValue;
  });
  const [usedCookieNames, setUsedCookieNames] = useState(() => {
    const initialEffectiveCookieRows = savedState?.cookieRows ?? [initialDefaultCookieRow];
    if (Array.isArray(initialEffectiveCookieRows) && initialEffectiveCookieRows.length > 0) {
      return Array.from(new Set(initialEffectiveCookieRows.map(row => row.cookieName)));
    }
    return ['Cookie Name']; // Default if cookieRows initializes to its default
  });

  // Sets state
  const [useSets, setUseSets] = useState(() => savedState?.useSets ?? false);
  const [setRows, setSetRows] = useState(() => {
    const rows = savedState?.setRows;
    return Array.isArray(rows) && rows.length > 0 
      ? rows 
      : [{ id: uuidv4(), setName: 'Set 1', cookieId: '', count: 1 }];
  });
  const [usedSets, setUsedSets] = useState(() => {
    const rows = savedState?.setRows; // Source from savedState if valid
    if (Array.isArray(rows) && rows.length > 0) {
      return Array.from(new Set(rows.map(row => row.setName)));
    }
    return ['Set 1']; // Default if setRows initializes to its default
  });

  // Dimensions state
  const [dimensionsRows, setDimensionsRows] = useState(() => {
    const initialEffectiveCookieRows = savedState?.cookieRows ?? [initialDefaultCookieRow];
    const savedDims = savedState?.dimensionsRows;

    return initialEffectiveCookieRows.map(cookieRow => {
      const existingDimRow = Array.isArray(savedDims) ? savedDims.find(dr => dr.cookieId === cookieRow.id) : null;
      if (existingDimRow) return existingDimRow;
      return {
        id: uuidv4(),
        cookieId: cookieRow.id,
        shapeType: '',
        width: '',
        length: '',
        diameter: '',
        area: 0,
        icingNeeded: 0
      };
    });
  });

  // Color related state
  const [colorNames, setColorNames] = useState(() => {
    const names = savedState?.colorNames;
    return Array.isArray(names) && names.length > 0 
      ? names 
      : ['White']; // Default color list
  });
  const [colorRows, setColorRows] = useState(() => {
    const initialEffectiveCookieRows = savedState?.cookieRows ?? [initialDefaultCookieRow];
    const initialEffectiveColorNames = savedState?.colorNames ?? ['White'];
    const savedColors = savedState?.colorRows;

    return initialEffectiveCookieRows.map(cookieRow => {
      const existingColorRow = Array.isArray(savedColors) ? savedColors.find(cr => cr.cookieId === cookieRow.id) : null;
      if (existingColorRow) {
        // Ensure percentages array matches current colorNames length
        const currentPercentages = existingColorRow.percentages || [];
        if (currentPercentages.length !== initialEffectiveColorNames.length) {
          const adjustedPercentages = Array(initialEffectiveColorNames.length).fill(0);
          for (let i = 0; i < Math.min(currentPercentages.length, initialEffectiveColorNames.length); i++) {
            adjustedPercentages[i] = currentPercentages[i];
          }
          // If no percentages existed and there are colors, default first to 100%
          if (currentPercentages.length === 0 && initialEffectiveColorNames.length > 0 && !adjustedPercentages.some(p => p > 0)){
              adjustedPercentages[0] = 100; // Default first color to 100% if all zero
          }
          return { ...existingColorRow, percentages: adjustedPercentages };
        }
        return existingColorRow;
      }
      // Create new default color row
      const defaultPercentages = Array(initialEffectiveColorNames.length).fill(0);
      if (initialEffectiveColorNames.length > 0) {
        defaultPercentages[0] = 100; // Default 100% to the first color
      }
      return {
        id: uuidv4(),
        cookieId: cookieRow.id,
        percentages: defaultPercentages,
      };
    });
  });
  const [useSliders, setUseSliders] = useState(() => savedState?.useSliders ?? true);

  // Orders state
  const [orderRows, setOrderRows] = useState(() => {
    const initialEffectiveCookieRows = savedState?.cookieRows ?? [initialDefaultCookieRow];
    const savedOrders = savedState?.orderRows;

    return initialEffectiveCookieRows.map(cookieRow => {
      const existingOrderRow = Array.isArray(savedOrders) ? savedOrders.find(or => or.cookieId === cookieRow.id) : null;
      if (existingOrderRow) return existingOrderRow;
      return {
        id: uuidv4(),
        cookieId: cookieRow.id,
        count: 1 // Default count to 1
      };
    });
  });

  // Results state
  const [copySuccess, setCopySuccess] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  // Other UI state
  const [showCalculationFields, setShowCalculationFields] = useState(() => savedState?.showCalculationFields ?? false);
  const [showImageAnalyzer, setShowImageAnalyzer] = useState(false);
  const [currentAnalyzingRow, setCurrentAnalyzingRow] = useState(null);

  // ADDED: Canonical color definitions and helpers
  const DEFAULT_COLOR_LIST = useMemo(() => ['Royal Blue', 'Leaf Green', 'Sunset Orange', 'Pretty Pink', 'Sunshine Yellow', 'Deep Purple', 'Plain White', 'Rich Black', 'Chocolate Brown', 'Sky Blue'], []);

  const PREDEFINED_COLOR_STYLES = useMemo(() => {
    const styles = {};
    DEFAULT_COLOR_LIST.forEach(name => {
      // Simple slug-like style for default, can be replaced with hex codes
      styles[name] = name.replace(/\s+/g, '').toLowerCase(); 
    });
    // Example of more specific styles:
    // styles['Royal Blue'] = '#4169E1';
    // styles['Leaf Green'] = '#228B22';
    return styles;
  }, [DEFAULT_COLOR_LIST]);

  const CANONICAL_COLOR_NAME_MAP = useMemo(() => 
    Object.fromEntries(
      DEFAULT_COLOR_LIST.map(name => [name.toLowerCase(), name])
    )
  , [DEFAULT_COLOR_LIST]);

  const getCanonicalColorName = useCallback((name) => {
    if (typeof name !== 'string' || !name.trim()) return ''; // Return empty for invalid input
    const trimmedName = name.trim();
    const canonical = CANONICAL_COLOR_NAME_MAP[trimmedName.toLowerCase()];
    if (canonical) return canonical;
    // Fallback: Title Case the input if not in map
    return trimmedName.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
  }, [CANONICAL_COLOR_NAME_MAP]);

  // Ensure colorNames state is initialized with canonical names
  // The setColorNames state dispatcher itself cannot be easily wrapped here for all calls.
  // Normalization should happen *before* calling setColorNames from components,
  // or in an effect that cleans up colorNames (can cause loops if not careful).

  // MODIFIED/ADDED: Color manipulation functions
  const addColor = useCallback(() => {
    setColorNames(prev => {
      const newNameBase = 'New Color';
      let newName = `${newNameBase} ${prev.length + 1}`;
      let i = prev.length + 1;
      // Ensure new name is unique after canonicalization
      while (prev.map(n => getCanonicalColorName(n)).includes(getCanonicalColorName(newName))) {
        i++;
        newName = `${newNameBase} ${i}`;
      }
      return [...prev, getCanonicalColorName(newName)];
    });
    setColorRows(prevCR => prevCR.map(cr => ({
      ...cr,
      percentages: [...(cr.percentages || Array(colorNames.length).fill(0)), 0]
    })));
  }, [setColorNames, setColorRows, colorNames, getCanonicalColorName]);

  const removeColor = useCallback((indexToRemove) => {
    setColorNames(prev => prev.filter((_, index) => index !== indexToRemove));
    setColorRows(prevCR => prevCR.map(cr => ({
      ...cr,
      percentages: (cr.percentages || []).filter((_, index) => index !== indexToRemove)
    })));
  }, [setColorNames, setColorRows]);

  const updateColorPercentage = useCallback((rowId, colorIndex, value) => {
    setColorRows(prevRows =>
      prevRows.map(row => {
        if (row.cookieId === rowId) { // Assuming rowId is cookieId from ColorsSection
          const newPercentages = [...(row.percentages || Array(colorNames.length).fill(0))];
          newPercentages[colorIndex] = Math.max(0, Math.min(100, Number(value) || 0));
          return { ...row, percentages: newPercentages };
        }
        return row;
      })
    );
  }, [setColorRows, colorNames]);

  const normalizeColorPercentages = useCallback(() => {
    setColorRows(prevRows => 
      prevRows.map(row => {
        const currentPercentages = row.percentages || [];
        if (currentPercentages.length === 0) return row; // Nothing to normalize
        const sum = currentPercentages.reduce((acc, p) => acc + (Number(p) || 0), 0);

        if (sum === 0) { // If all are 0, distribute 100 among them equally, or first one gets 100
          if (currentPercentages.length > 0) {
            const equalShare = round(100 / currentPercentages.length, 0);
            const normalized = Array(currentPercentages.length).fill(equalShare);
            let remainder = 100 - normalized.reduce((s, p) => s + p, 0);
            if (normalized.length > 0) normalized[0] += remainder; // Add remainder to first
            return { ...row, percentages: normalized };
          }
          return row; 
        }
        if (Math.abs(sum - 100) < 0.01) return row; // Already normalized or very close

        const normalized = currentPercentages.map(p => round(((Number(p) || 0) / sum) * 100, 0));
        let currentSum = normalized.reduce((acc, p) => acc + p, 0);
        const diff = 100 - currentSum;

        if (diff !== 0 && normalized.length > 0) {
          let adjustIndex = 0;
          // Try to add/subtract from non-zero elements first if possible
          const nonZeroIndices = normalized.map((p, i) => p > 0 ? i : -1).filter(i => i !== -1);
          if (nonZeroIndices.length > 0) {
            adjustIndex = nonZeroIndices[Math.floor(Math.random() * nonZeroIndices.length)]; // Distribute somewhat randomly
          } else {
            adjustIndex = Math.floor(Math.random() * normalized.length); // Fallback if all zero (though covered above)
          }
          normalized[adjustIndex] += diff;
          normalized[adjustIndex] = Math.max(0, Math.min(100, normalized[adjustIndex]));
          
          // Final check if sum is still off due to min/max, try to adjust others
          currentSum = normalized.reduce((acc, p) => acc + p, 0);
          if (Math.abs(currentSum - 100) > 0.01 && normalized.length > 1) {
            const secondDiff = 100 - currentSum;
            for (let i = 0; i < normalized.length; i++) {
              if (i !== adjustIndex) {
                const oldVal = normalized[i];
                normalized[i] = Math.max(0, Math.min(100, normalized[i] + secondDiff));
                const actualChange = normalized[i] - oldVal;
                if (Math.abs(actualChange - secondDiff) < 0.01) break; // Applied the difference
              }
            }
          }
        }
        return { ...row, percentages: normalized };
      })
    );
  }, [setColorRows]);

  const getColorStyle = useCallback((name) => {
    if (typeof name !== 'string' || !name.trim()) return { backgroundColor: 'transparent', color: '#000', padding: '2px 5px', borderRadius: '3px', display: 'inline-block', marginRight: '5px', border: '1px solid #ccc' };
    const canonicalName = getCanonicalColorName(name);
    const styleValue = PREDEFINED_COLOR_STYLES[canonicalName] || canonicalName.replace(/\s+/g, '').toLowerCase();
    
    let textColor = '#000000';
    // Basic contrast check (very naive)
    const lc = styleValue.toLowerCase(); 
    if (lc.startsWith('#')) { // hex
      const r = parseInt(lc.substr(1, 2), 16) || 0;
      const g = parseInt(lc.substr(3, 2), 16) || 0;
      const b = parseInt(lc.substr(5, 2), 16) || 0;
      if ((r*0.299 + g*0.587 + b*0.114) < 128) textColor = '#FFFFFF';
    } else if (['black', 'blue', 'green', 'purple', 'brown', 'navy', 'maroon', 'dark', 'deep'].some(darkWord => lc.includes(darkWord))) {
      textColor = '#FFFFFF';
    }
    return { backgroundColor: styleValue, color: textColor, padding: '2px 5px', borderRadius: '3px', display: 'inline-block', marginRight: '5px', border: '1px solid #ccc' };
  }, [getCanonicalColorName, PREDEFINED_COLOR_STYLES]);

  // Section visibility helper
  const isSectionVisible = useCallback((sectionName) => {
    return activeSection === sectionName;
  }, [activeSection]);

  // Function to navigate to the next section
  const goToNextSection = useCallback((currentSection) => {
    const currentIndex = sections.findIndex(section => section.id === currentSection);
    if (currentIndex >= 0 && currentIndex < sections.length - 1) {
      setActiveSection(sections[currentIndex + 1].id);
      // Scroll to top of the page for better UX
      window.scrollTo(0, 0);
    }
  }, [sections, setActiveSection]);

  // ==================== HELPER FUNCTIONS & EFFECTS ====================
  
  // Load state helper
  const loadState = useCallback((state) => {
    isSyncingRef.current = true;
    requestAnimationFrame(() => {
      isSyncingRef.current = false;
    });

    if (state.useDefaultReference !== undefined) setUseDefaultReference(state.useDefaultReference);
    if (state.reference !== undefined) setReference(state.reference);
    if (state.useSets !== undefined) setUseSets(state.useSets);
    if (state.cookieRows !== undefined) setCookieRows(state.cookieRows);
    if (state.setRows !== undefined) setSetRows(state.setRows);
    if (state.dimensionsRows !== undefined) setDimensionsRows(state.dimensionsRows);
    if (state.colorNames !== undefined) setColorNames(state.colorNames);
    if (state.colorRows !== undefined) setColorRows(state.colorRows);
    if (state.orderRows !== undefined) setOrderRows(state.orderRows);
    if (state.useSliders !== undefined) setUseSliders(state.useSliders);
    if (state.showCalculationFields !== undefined) setShowCalculationFields(state.showCalculationFields);
  }, []);

  // Check if the device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Effect to update section completion status on state changes
  useEffect(() => {
    const currentState = {
      useDefaultReference,
      reference,
      useSets,
      cookieRows,
      setRows,
      dimensionsRows,
      colorNames,
      colorRows,
      orderRows,
      useSliders,
      showCalculationFields,
      usedSets,
      usedCookieNames
    };

    // Calculate section completion status
    const status = calculateSectionCompletionStatus(currentState);
    setSectionCompletionStatus(status);

    // Notify parent of state changes if needed
    const isStateSubstantiallyPopulated = currentState.cookieRows && currentState.cookieRows.length > 0;

    if (didMountRef.current && // Ensure not initial mount pass for *this specific effect's purpose*
        !isSyncingRef.current && 
        onStateChange && 
        isStateSubstantiallyPopulated && // Only send if data seems meaningful
        !deepEqual(currentState, prevSavedStateRef.current)) {
      const timerId = setTimeout(() => {
        console.log('[ProCalculatorContext DEBUG] About to call onStateChange. currentState:', currentState);
        onStateChange(currentState);
        prevSavedStateRef.current = currentState;
      }, 0);
      return () => clearTimeout(timerId);
    }
  }, [
    useDefaultReference,
    reference,
    useSets,
    cookieRows,
    setRows,
    dimensionsRows,
    colorNames,
    colorRows,
    orderRows,
    useSliders,
    showCalculationFields,
    usedSets,
    usedCookieNames,
    onStateChange
  ]);

  // Effect to load state when savedState prop changes
  useEffect(() => {
    if (savedState && !deepEqual(savedState, prevSavedStateRef.current)) {
      loadState(savedState);
      prevSavedStateRef.current = savedState;

      // Calculate section completion status when loading saved state
      const status = calculateSectionCompletionStatus(savedState);
      setSectionCompletionStatus(status);
      didMountRef.current = true; // Indicate that initial prop-based state loading has occurred
    }
  }, [savedState, loadState]);

  // --- Effect to synchronize colorRows with cookieRows and colorNames ---
  useEffect(() => {
    setColorRows(prevColorRows => {
      let newColorRows = [...prevColorRows];
      let changed = false;

      const cookieRowIds = cookieRows.map(cr => cr.id);
      const colorRowCookieIds = newColorRows.map(clr => clr.cookieId);

      // 1. Add new color rows for new cookieRows
      cookieRows.forEach(cookieRow => {
        if (!colorRowCookieIds.includes(cookieRow.id)) {
          const initialPercentages = Array(colorNames.length).fill(0);
          if (colorNames.length > 0) {
            initialPercentages[0] = 100; // Default 100% to the first color
          }
          newColorRows.push({
            id: uuidv4(), // Unique ID for the color row itself
            cookieId: cookieRow.id,
            percentages: initialPercentages,
          });
          changed = true;
        }
      });

      // 2. Remove color rows for deleted cookieRows
      const filteredColorRows = newColorRows.filter(clr => cookieRowIds.includes(clr.cookieId));
      if (filteredColorRows.length !== newColorRows.length) {
        newColorRows = filteredColorRows;
        changed = true;
      }

      // 3. Adjust percentages array length for each colorRow if colorNames changed
      newColorRows = newColorRows.map(colorRow => {
        const currentPercentages = colorRow.percentages || [];
        if (currentPercentages.length !== colorNames.length) {
          const adjustedPercentages = Array(colorNames.length).fill(0);
          for (let i = 0; i < Math.min(currentPercentages.length, colorNames.length); i++) {
            adjustedPercentages[i] = currentPercentages[i];
          }
          // If no percentages existed and there are colors, default first to 100%
          if (currentPercentages.length === 0 && colorNames.length > 0 && !adjustedPercentages.some(p => p > 0)){
              adjustedPercentages[0] = 100;
          }
          changed = true;
          return { ...colorRow, percentages: adjustedPercentages };
        }
        return colorRow;
      });

      return changed ? newColorRows : prevColorRows;
    });
  }, [cookieRows, colorNames, setColorRows]);

  // --- Effect to synchronize orderRows with cookieRows ---
  useEffect(() => {
    setOrderRows(prevOrderRows => {
      let newOrderRows = [...prevOrderRows];
      let changed = false;

      const orderRowCookieIds = newOrderRows.map(or => or.cookieId);

      cookieRows.forEach(cookieRow => {
        if (!orderRowCookieIds.includes(cookieRow.id)) {
          newOrderRows.push({
            id: uuidv4(),       // Unique ID for the order row itself
            cookieId: cookieRow.id,
            count: 1,           // Default count to 1
            // Add any other default fields for an order row here if necessary
          });
          changed = true;
        }
      });

      // Ensure all existing order rows still have a corresponding cookieRow
      // (removeCookieRow handles direct deletion, this is a safety sync)
      const validCookieRowIds = cookieRows.map(cr => cr.id);
      const syncedOrderRows = newOrderRows.filter(or => validCookieRowIds.includes(or.cookieId));
      if (syncedOrderRows.length !== newOrderRows.length) {
        newOrderRows = syncedOrderRows;
        changed = true;
      }

      return changed ? newOrderRows : prevOrderRows;
    });
  }, [cookieRows, setOrderRows]);

  // --- Effect to synchronize dimensionsRows with cookieRows ---
  useEffect(() => {
    setDimensionsRows(prevDimensionsRows => {
      let newDimensionsRows = [];
      let changed = false;

      const existingDimensionRowCookieIds = prevDimensionsRows.map(dr => dr.cookieId);

      cookieRows.forEach(cookieRow => {
        const existingDimRow = prevDimensionsRows.find(dr => dr.cookieId === cookieRow.id);
        if (existingDimRow) {
          newDimensionsRows.push(existingDimRow);
        } else {
          // Create a new dimensions row for this cookie
          newDimensionsRows.push({
            id: uuidv4(), // Dimension row's own unique ID
            cookieId: cookieRow.id, // Link to the cookie's ID
            shapeType: '',
            width: '',
            length: '',
            diameter: '',
            area: 0,
            icingNeeded: 0
          });
          changed = true;
        }
      });

      // Check if the length has changed (e.g. a cookie was deleted, so a dimension row should be removed)
      if (newDimensionsRows.length !== prevDimensionsRows.length) {
        changed = true;
      }

      // If no changes in existing rows but a new one was added, or one was removed.
      // More robust check: compare item by item if necessary, but length check covers deletions.
      // And new items are covered by 'changed' flag.

      return changed ? newDimensionsRows : prevDimensionsRows;
    });
  }, [cookieRows, setDimensionsRows]);

  // --- Effect to calculate reference icing density and update all dimensions rows --- 
  useEffect(() => {
    const currentRef = useDefaultReference ? defaultReference : reference;
    const refWidth = parseFloat(currentRef.width) || 0;
    const refLength = parseFloat(currentRef.length) || 0;
    const refWeightNoIcing = parseFloat(currentRef.weightNoIcing) || 0;
    const refWeight = parseFloat(currentRef.weight) || 0;
    
    // *** Log Input Values ***
    // console.log(`%cProContext Ref Density CALC INPUTS: W=${refWidth}, L=${refLength}, Weight=${refWeight}, WeightNoIcing=${refWeightNoIcing}`, 'color: cyan');

    const refArea = calculateArea('rectangle', refWidth, refLength); // Assuming rectangle for reference
    const refIcingWeight = refWeight - refWeightNoIcing;
    
    // *** Log Intermediate Values ***
    // console.log(`%cProContext Ref Density CALC INTERMEDIATE: Area=${refArea.toFixed(4)}, IcingWeight=${refIcingWeight.toFixed(2)}`, 'color: cyan');

    let newIcingPerArea = 0;
    if (refArea > 0 && refIcingWeight > 0) {
      newIcingPerArea = refIcingWeight / refArea; // g/in²
    }
    referenceIcingPerAreaRef.current = newIcingPerArea;
    // console.log(`%cProContext: Reference Icing Density Updated & Stored: ${referenceIcingPerAreaRef.current.toFixed(4)} g/in²`, 'color: blue');

    // Now, recalculate icingNeeded for all existing dimension rows
    setDimensionsRows(prevRows => {
      let hasChanged = false; // Flag to track if any row actually changed
      const newRows = prevRows.map(row => {
        // Only update if area is valid and reference density is positive
        const currentArea = parseFloat(row.area) || 0;
        const newIcingNeeded = currentArea > 0 && newIcingPerArea > 0 
                             ? round(currentArea * newIcingPerArea, 2) 
                             : 0; // Set to 0 if area invalid or density non-positive
                             
        // Compare calculated value with existing value (treat existing as number)
        const currentRowIcing = parseFloat(row.icingNeeded) || 0;
                             
        if (currentRowIcing !== newIcingNeeded) {
             // console.log(`%cProContext Row ${row.id} Update Icing (Ref Change): ${currentRowIcing} -> ${newIcingNeeded}`, 'color: purple');
             hasChanged = true; // Mark that a change occurred
             return { ...row, icingNeeded: newIcingNeeded };
        }
        return row; // Return the original row object if no change
      });
      
      // Only return the new array if something actually changed to prevent loops
      return hasChanged ? newRows : prevRows;
    });

  }, [reference, useDefaultReference, defaultReference]); // Rerun when reference details change

  // Function to update a specific dimensions row
  const updateDimensionsRow = useCallback((id, field, value) => {
    setDimensionsRows(prevRows => {
      let rowEffectivelyChanged = false; // Flag if the target row's data truly changed
      const newRows = prevRows.map(row => {
        if (row.id === id) {
          const originalRow = { ...row }; // Keep original for comparison
          const updatedRow = { ...row, [field]: value };
          let needsRecalculation = false;

          // Check if the edited field actually changed the value
          let fieldChanged = originalRow[field] !== updatedRow[field];

          // If a dimension-affecting field changed, recalculate area and icing
          if (['shapeType', 'width', 'length', 'diameter'].includes(field)) {
            needsRecalculation = true;
            const { shapeType, width, length, diameter } = updatedRow;
            const newArea = calculateArea(shapeType, parseFloat(width) || 0, parseFloat(length) || 0, parseFloat(diameter) || 0);
            const currentIcingPerArea = referenceIcingPerAreaRef.current;
            
            // console.log(`%cProContext Row ${id} Icing Calc: Area=${newArea.toFixed(4)}, Density=${currentIcingPerArea.toFixed(4)}`, 'color: magenta');
            
            const calculatedArea = round(newArea, 4); // Round area
            const calculatedIcing = (newArea > 0 && currentIcingPerArea > 0)
                                     ? round(newArea * currentIcingPerArea, 2)
                                     : 0; // Reset icing if area invalid or no reference density
                                     
            // Check if calculated values differ from original
            const areaChanged = calculatedArea !== (parseFloat(originalRow.area) || 0);
            const icingChanged = calculatedIcing !== (parseFloat(originalRow.icingNeeded) || 0);
            
            if (areaChanged || icingChanged) {
              // console.log(`%cProContext: Recalculated row ${id}: Area=${calculatedArea}(${areaChanged?'changed':'same'}), Icing=${calculatedIcing}(${icingChanged?'changed':'same'})`, 'color: green');
              updatedRow.area = calculatedArea;
              updatedRow.icingNeeded = calculatedIcing;
            } else {
                // If calculated values are same, restore original calculated values
                // but keep the trigger field's new value if it changed
                updatedRow.area = originalRow.area;
                updatedRow.icingNeeded = originalRow.icingNeeded;
            }
             // Row effectively changed if the input field changed OR calculated values changed
            rowEffectivelyChanged = fieldChanged || areaChanged || icingChanged;
          } else {
             // For non-dimension fields, effective change is just field change
            rowEffectivelyChanged = fieldChanged;
          }

          return rowEffectivelyChanged ? updatedRow : originalRow; // Return updated only if changed
        }
        return row; // Return other rows unmodified
      });
      
      // Determine if the array identity needs to change
      const arrayChanged = newRows.some((newRow, index) => newRow !== prevRows[index]);

      // Trigger state change callback if provided and array changed
      if (arrayChanged && onStateChange) {
        onStateChange(prev => ({ ...prev, dimensionsRows: newRows }));
      }
      // Return new array only if changed
      return arrayChanged ? newRows : prevRows;
    });
  }, [onStateChange]); // referenceIcingPerAreaRef is stable

  // Function to sort dimensions rows alphabetically by cookie name
  const sortDimensionsRows = useCallback(() => {
    setDimensionsRows(prevRows => {
      const sortedRows = [...prevRows];
      sortedRows.sort((a, b) => {
        const cookieNameA = cookieRows.find(row => row.id === a.cookieId)?.cookieName || '';
        const cookieNameB = cookieRows.find(row => row.id === b.cookieId)?.cookieName || '';
        return cookieNameA.localeCompare(cookieNameB);
      });
      return sortedRows;
    });
  }, [cookieRows]);

  // Handle reference change
  const handleReferenceChange = useCallback((field, value) => {
    setReference(prev => ({ ...prev, [field]: value }));
  }, []);

  // Remove cookie row
  const removeCookieRow = useCallback((idToRemove) => {
    setCookieRows(prevRows => prevRows.filter(row => row.id !== idToRemove));
    setOrderRows(prevRows => prevRows.filter(row => row.cookieId !== idToRemove));
    setColorRows(prevRows => prevRows.filter(row => row.cookieId !== idToRemove));
    // Update derived state
    setUsedCookieNames(prev => prev.filter(name => name !== cookieRows.find(row => row.id === idToRemove)?.cookieName));
  }, [cookieRows]);

  // Add a new cookie row
  const addCookieRow = useCallback(() => {
    setCookieRows(prev => {
      const generatedId = uuidv4(); 
      const newRows = [...prev, { id: generatedId, cookieName: '' }];
      return newRows;
    });
  }, [setCookieRows]);

  // Update cookie row
  const updateCookieRow = useCallback((id, field, value) => {
    setCookieRows((prev) => {
      const newRows = prev.map((row) => (row.id === id ? { ...row, [field]: value } : row));
      if (field === 'cookieName') {
        setUsedCookieNames(Array.from(new Set(newRows.map(r => r.cookieName)))); // Update derived state
      }
      return newRows;
    });
  }, [setCookieRows, setUsedCookieNames]);

  // Update set row value
  const updateSetRowValue = useCallback((id, field, value) => {
    setSetRows((prev) => {
      const newRows = prev.map((row) => (row.id === id ? { ...row, [field]: value } : row));
      if (field === 'setName') {
        setUsedSets(Array.from(new Set(newRows.map(r => r.setName)))); // Update derived state
      }
      return newRows;
    });
  }, []);

  // Update dimensions row value
  const updateDimensionsRowValue = useCallback((id, field, value) => {
    setDimensionsRows(prevRows => prevRows.map(row => row.id === id ? { ...row, [field]: value } : row));
  }, []);

  // Update color row value
  const updateColorRowValue = useCallback((id, field, value) => {
    setColorRows(prevRows => prevRows.map(row => row.id === id ? { ...row, [field]: value } : row));
  }, []);

  // Update order row value
  const updateOrderRowValue = useCallback((id, field, value) => {
    setOrderRows(prevRows => prevRows.map(row => row.id === id ? { ...row, [field]: value } : row));
  }, []);

  // Update order row
  const updateOrderRow = useCallback((cookieId, newCount) => {
    setOrderRows(prevRows => {
      const rowIndex = prevRows.findIndex(row => row.cookieId === cookieId);
      if (rowIndex !== -1) {
        const updatedRow = { ...prevRows[rowIndex], count: Math.max(0, Number(newCount) || 0) };
        const newRows = [...prevRows];
        newRows[rowIndex] = updatedRow;
        return newRows;
      } else {
        // This case should ideally be handled by the useEffect that syncs orderRows with cookieRows,
        // creating the row with a default count of 1. If we reach here, it means a cookie exists
        // but its orderRow wasn't created, or it's an unexpected situation.
        // For robustness, we can create it here, but it might indicate a gap in the sync effect.
        // However, the user wants new rows to default to 1. The sync effect does this.
        // This function should primarily update existing ones.
        const newRow = {
          id: uuidv4(),
          cookieId: cookieId,
          count: Math.max(0, Number(newCount) || 0), // Use newCount, or 0 if invalid
                                                    // The sync effect should have set it to 1 initially.
        };
        // console.warn(`updateOrderRow created a new order row for cookieId: ${cookieId}. This might be unexpected.`);
        return [...prevRows, newRow];
      }
    });
  }, [setOrderRows]);

  // ==================== CONTEXT VALUE ====================
  // Create context value object
  const contextValue = useMemo(() => ({
    // User and subscription info
    user,
    isSubscribed,
    isInFreeTrial,
    subscriptionStatus,
    subscriptionDetails,
    
    // Section navigation
    isSectionVisible,
    activeSection,
    setActiveSection,
    sections,
    sectionCompletionStatus,
    goToNextSection,
    
    // Mobile detection
    isMobile,
    
    // Reference cookie
    reference,
    setReference,
    defaultReference,
    useDefaultReference,
    setUseDefaultReference,
    handleReferenceChange,
    
    // Cookie rows
    cookieRows,
    setCookieRows,
    usedCookieNames,
    setUsedCookieNames,
    updateCookieRow,
    addCookieRow,
    removeCookieRow,
    
    // Sets
    useSets,
    setUseSets,
    setRows,
    setSetRows,
    usedSets,
    setUsedSets,
    
    // Dimensions
    dimensionsRows,
    setDimensionsRows,
    updateDimensionsRow,
    
    // Colors
    colorNames,
    setColorNames, // This is the original dispatcher from useState
    colorRows,
    setColorRows,
    useSliders,
    setUseSliders,
    // ADDED/MODIFIED Color functions to context:
    getColorStyle,
    addColor,
    removeColor,
    updateColorPercentage,
    normalizeColorPercentages,
    getCanonicalColorName,
    
    // Orders
    orderRows,
    setOrderRows,
    updateOrderRow,
    
    // Results
    copySuccess,
    setCopySuccess,
    exportSuccess,
    setExportSuccess,
    
    // Toggle for showing calculation fields
    showCalculationFields,
    setShowCalculationFields,
  }), [
    user,
    isSubscribed,
    isInFreeTrial,
    subscriptionStatus,
    subscriptionDetails,
    isSectionVisible,
    activeSection,
    setActiveSection,
    sections,
    sectionCompletionStatus,
    isMobile,
    reference,
    setReference,
    defaultReference,
    useDefaultReference,
    setUseDefaultReference,
    handleReferenceChange,
    cookieRows,
    setCookieRows,
    usedCookieNames,
    setUsedCookieNames,
    updateCookieRow,
    addCookieRow,
    removeCookieRow,
    useSets,
    setUseSets,
    setRows,
    setSetRows,
    usedSets,
    setUsedSets,
    dimensionsRows,
    setDimensionsRows,
    updateDimensionsRow,
    colorNames,
    setColorNames,
    colorRows,
    setColorRows,
    useSliders,
    setUseSliders,
    getColorStyle,
    addColor,
    removeColor,
    updateColorPercentage,
    normalizeColorPercentages,
    getCanonicalColorName,
    orderRows,
    setOrderRows,
    updateOrderRow,
    copySuccess,
    setCopySuccess,
    exportSuccess,
    setExportSuccess,
    showCalculationFields,
    setShowCalculationFields,
  ]);

  // Return the Provider
  return (
    <ProCalculatorContext.Provider value={contextValue}>
      {children}
    </ProCalculatorContext.Provider>
  );
}

// Export the context and the provider
export { ProCalculatorContext };
export default ProCalculatorProvider;
